import { Component, HostBinding, Input } from '@angular/core';

/**
 * Empty State Component
 *
 * Displays a consistent empty state UI when no data is available.
 * Uses Lucide icons.
 *
 * Usage:
 * ```html
 * <app-empty-state
 *   icon="inbox"
 *   title="common.no_data"
 *   description="common.try_again">
 * </app-empty-state>
 *
 * <app-empty-state
 *   icon="search"
 *   title="No results found"
 *   description="Try a different search term">
 * </app-empty-state>
 * ```
 */
@Component({
  selector: 'app-empty-state',
  standalone: false,
  templateUrl: './empty-state.component.html',
  styleUrl: './empty-state.component.scss'
})
export class EmptyStateComponent {
  /** Icon type: 'lucide' for Lucide icons (default). 'mdi' and 'feather' kept for backward compatibility. */
  @Input() iconType: 'mdi' | 'feather' | 'lucide' = 'lucide';

  /** Lucide icon name (e.g., 'inbox', 'search') */
  @Input() icon = 'inbox';

  /** Main title text (translation key or plain text) */
  @Input() title = 'No data available';

  /** Optional description text below title (translation key or plain text) */
  @Input() description = '';

  /** Whether to show an action button */
  @Input() showAction = false;

  /** Action button label */
  @Input() actionLabel = 'Take action';

  /** Size variant: 'sm' | 'md' | 'lg' */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /** Custom CSS class for additional styling */
  @Input() customClass = '';

  /**
   * Clears the native HTML `title` attribute on the host element.
   * Without this, Angular's static attribute binding (`title="some.key"`) would set
   * the browser tooltip to the raw translation key instead of the translated text.
   */
  @HostBinding('attr.title')
  get hostTitle(): null {
    return null;
  }

  /**
   * Resolves the icon name for lucide-icon.
   * Handles legacy "mdi mdi-xxx" format by stripping the prefix
   * and mapping to the closest Lucide equivalent.
   */
  get resolvedIcon(): string {
    // Already a Lucide icon name (no "mdi" prefix)
    if (!this.icon.startsWith('mdi')) {
      return this.icon;
    }

    // Extract MDI icon name: "mdi mdi-inbox" → "inbox", "mdi mdi-file-document-outline" → "file-document-outline"
    const mdiMatch = this.icon.match(/mdi-(.+)/);
    const mdiName = mdiMatch ? mdiMatch[1] : this.icon;

    // Map common MDI names to Lucide equivalents
    const mdiToLucide: Record<string, string> = {
      'inbox': 'inbox',
      'inbox-outline': 'inbox',
      'file-document-outline': 'file-text',
      'calendar-blank-outline': 'calendar',
      'account-group-outline': 'users',
      'door-open': 'door-open',
      'hiking': 'mountain-snow',
      'palette-outline': 'palette',
      'message-text-outline': 'message-square',
      'star-outline': 'star',
      'calendar-clock': 'calendar-clock',
      'bed-outline': 'bed-double',
      'chart-timeline-variant': 'chart-line',
      'clock-fast': 'clock',
      'room-service-outline': 'concierge-bell',
    };

    return mdiToLucide[mdiName] ?? 'circle-help';
  }
}
